import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Wraps a manually-triggered async service call (create/update/delete/
 * dispatch/download) with `{ run, isLoading, error, data, reset }` state,
 * mirroring RTK Query's mutation-hook ergonomics without depending on
 * Redux. `run(...)` returns the resolved value so callers can `await` it
 * for a follow-up action (e.g. close a modal only on success).
 */
export function useApiMutation(mutationFn) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const run = useCallback(
    async (...args) => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await mutationFn(...args);
        if (mounted.current) setData(result);
        return result;
      } catch (err) {
        if (mounted.current) setError(err);
        throw err;
      } finally {
        if (mounted.current) setIsLoading(false);
      }
    },
    [mutationFn]
  );

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setIsLoading(false);
  }, []);

  return { run, data, error, isLoading, reset };
}
